import React, { useRef, useEffect } from 'react';
import { getConfig } from '../../utils/runtimeConfig';

/**
 * Reusable Turnstile CAPTCHA widget — renders exactly ONCE via window.turnstile.render().
 * Callbacks are stored in refs so the widget is never torn down/re-created on parent re-renders.
 *
 * Site key comes from runtime config (GET /api/config).
 */
const TurnstileWidget = ({ onVerify, onError, onExpire, size = 'normal', theme = 'light', className = '' }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const mountedRef = useRef(true);

  // Store callbacks in refs to avoid re-render cycles
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  const onExpireRef = useRef(onExpire);
  onVerifyRef.current = onVerify;
  onErrorRef.current = onError;
  onExpireRef.current = onExpire;

  useEffect(() => {
    mountedRef.current = true;
    let retryTimer = null;

    const renderWidget = () => {
      if (!mountedRef.current || !containerRef.current) return;

      if (typeof window.turnstile === 'undefined') {
        retryTimer = setTimeout(renderWidget, 300);
        return;
      }

      const cfg = getConfig();
      const siteKey = cfg.turnstile_site_key;

      if (!siteKey) {
        console.error('Turnstile: No site key available from runtime config');
        return;
      }

      // Only render once
      if (widgetIdRef.current !== null) return;

      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onVerifyRef.current?.(token),
          'error-callback': () => onErrorRef.current?.(),
          'expired-callback': () => onExpireRef.current?.(),
          theme,
          size,
        });
      } catch (err) {
        console.error('Turnstile render error:', err);
      }
    };

    renderWidget();

    return () => {
      mountedRef.current = false;
      if (retryTimer) clearTimeout(retryTimer);
      if (widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) {}
        widgetIdRef.current = null;
      }
    };
  // Only run once on mount — callbacks stored in refs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={containerRef} className={className} data-testid="turnstile-widget" />
  );
};

export default TurnstileWidget;
